import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { CloudArrowUp, GoogleDriveLogo, Link as LinkIcon } from "@phosphor-icons/react";

export default function BackupTab() {
    const [status, setStatus] = useState(null);
    const [backups, setBackups] = useState([]);
    const [connecting, setConnecting] = useState(false);
    const [backingUp, setBackingUp] = useState(false);

    const load = () => {
        Promise.all([api.get("/admin/drive/status"), api.get("/admin/backups")])
            .then(([statusRes, backupsRes]) => {
                setStatus(statusRes.data);
                setBackups(backupsRes.data);
            })
            .catch(() => toast.error("Gagal memuat status backup"));
    };

    useEffect(() => {
        load();
    }, []);

    const connect = async () => {
        setConnecting(true);
        try {
            const { data } = await api.get("/admin/drive/connect");
            window.location.href = data.authorization_url;
        } catch (err) {
            toast.error(err.response?.data?.detail || "Gagal memulai koneksi Google Drive");
            setConnecting(false);
        }
    };

    const backupNow = async () => {
        setBackingUp(true);
        try {
            const { data } = await api.post("/admin/backup");
            toast.success(`Backup tersimpan di Google Drive: ${data.filename}`);
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Backup gagal");
        } finally {
            setBackingUp(false);
        }
    };

    if (!status) {
        return <p className="text-racing-muted" data-testid="backup-loading">Memuat status backup...</p>;
    }

    return (
        <div data-testid="backup-tab">
            <h2 className="font-display uppercase text-2xl text-neutral-900 mb-2">Backup Data</h2>
            <p className="text-sm text-racing-muted mb-8 max-w-2xl">
                Satu klik untuk menyimpan seluruh data (akun orang tua, data atlet, prestasi, catatan waktu, rapot, absensi, iuran, dan laporan keuangan) sebagai file Excel multi-sheet ke Google Drive <span className="font-medium text-neutral-900">mandalikainlineskate@gmail.com</span>.
            </p>

            <div className="border border-racing-line bg-white p-6 md:p-8 mb-8" data-testid="backup-status-card">
                <div className="flex flex-wrap items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                        <span className="w-12 h-12 bg-racing-surface border border-racing-line flex items-center justify-center">
                            <GoogleDriveLogo size={26} weight="duotone" className="text-racing-red" />
                        </span>
                        <div>
                            <p className="text-[10px] uppercase tracking-[0.2em] text-racing-muted">Status Google Drive</p>
                            <p className="font-medium text-neutral-900 mt-1" data-testid="backup-drive-status">
                                {!status.configured
                                    ? "Kredensial Google belum dipasang di server"
                                    : status.connected
                                        ? `Terhubung${status.email ? ` sebagai ${status.email}` : ""}`
                                        : "Belum terhubung"}
                            </p>
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-3">
                        <a
                            href={`${api.defaults.baseURL}/admin/backup/download`}
                            data-testid="backup-download-button"
                            className="flex items-center gap-2 bg-racing-red hover:bg-racing-redhover text-white text-xs uppercase tracking-[0.2em] px-6 py-3.5 transition-colors duration-300"
                        >
                            <CloudArrowUp size={16} weight="bold" /> Unduh Backup (Excel)
                        </a>
                        {!status.connected ? (
                            <button
                                onClick={connect}
                                disabled={connecting || !status.configured}
                                data-testid="backup-connect-button"
                                className="flex items-center gap-2 border border-racing-red text-racing-red hover:bg-racing-red hover:text-white disabled:opacity-50 text-xs uppercase tracking-[0.2em] px-5 py-3 transition-colors duration-300"
                            >
                                <LinkIcon size={16} /> {connecting ? "Mengalihkan..." : "Hubungkan Google Drive"}
                            </button>
                        ) : (
                            <button
                                onClick={backupNow}
                                disabled={backingUp}
                                data-testid="backup-now-button"
                                className="flex items-center gap-2 border border-racing-red text-racing-red hover:bg-racing-red hover:text-white disabled:opacity-50 text-xs uppercase tracking-[0.2em] px-5 py-3 transition-colors duration-300"
                            >
                                <GoogleDriveLogo size={16} /> {backingUp ? "Mengirim ke Drive..." : "Kirim Backup ke Drive"}
                            </button>
                        )}
                    </div>
                </div>
                {!status.configured && (
                    <p className="mt-4 text-xs text-racing-muted" data-testid="backup-config-hint">
                        Hubungi pengelola server untuk memasang GOOGLE_CLIENT_ID dan GOOGLE_CLIENT_SECRET agar koneksi Google Drive aktif.
                    </p>
                )}
            </div>

            <h3 className="text-xs uppercase tracking-[0.25em] text-racing-muted mb-4">Riwayat Backup</h3>
            {backups.length === 0 ? (
                <p className="border border-racing-line bg-white p-6 text-sm text-racing-muted" data-testid="backup-history-empty">Belum ada backup yang dibuat.</p>
            ) : (
                <div className="border border-racing-line bg-white" data-testid="backup-history-list">
                    {backups.map((b, i) => (
                        <div key={b.id} className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-b border-racing-line last:border-0" data-testid={`backup-row-${i}`}>
                            <div>
                                <p className="font-medium text-sm text-neutral-900">{b.filename}</p>
                                <p className="text-xs text-racing-muted mt-0.5">{(b.created_at || "").slice(0, 16).replace("T", " ")} • oleh {b.created_by}</p>
                            </div>
                            {b.web_view_link && (
                                <a href={b.web_view_link} target="_blank" rel="noopener noreferrer" data-testid={`backup-link-${i}`} className="text-xs uppercase tracking-[0.2em] text-racing-red hover:text-racing-redhover">
                                    Buka di Drive
                                </a>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
